import { WorkerManager } from './manager';
import { Application } from '../application/application';
import { CacheKey, ScalarMap, ImpactMap } from '../scalar-field/scalar-field';
import { LoadIndicatedCacheKey } from '../scalar-field/data';
import { Isoline, ScalarFieldSVGResult } from '../scalar-field/isoline';
import { ModelRunProfile, ModelProject, ModelRun, getModelingMonoHueInterpolator } from '../modeling/modeling-common';
import { GoiDatum } from '../poi/poi-common';
import { TimedSVGResult } from '../map/svg-overlay-result';
import { MapOverlaySVGResult, markupToSVGElemResult } from '../map/svg-map-overlay-result';
import { constraintColorInterpolator, extractMonoHueInterpolator, getCustomColorInterpolator } from '../util/color';
import { LocationTime } from '../data-source/types';
import { SVGRoot, wrapInSVG } from '../svg/svg'
import { Observable } from 'rxjs';
import { RepresentationDefinition, CacheEntry, ProfileMap } from './scalar-field-worker';
import { Polygon } from '../geometry/polygon';
import { Point2D } from '../geometry/geometry';
import ScalarFieldWorker from 'worker-loader!./scalar-field-worker';
import { ModellingGrid, WindProfileInterval } from '@mapp/r-mapp-lib';
import { ImpactMonitorDatum } from '../modeling/modeling';
import { processImpactMonitors } from '../modeling/container-modeling';


export enum CombineMode {
	none = 'none',
	category = 'category',
	all = 'all'
}
export enum RenderMode {
	line = 'line',
	band = 'band',
	line_band = 'line_band'
}
export enum ColorMode {
	palette = 'palette',
	category = 'category'
}

export interface OverlayDistribution {
	elemId: string, dataIds: string[]
}
/**
 * The OverlayDistributions keyed by the different CombineModes
 */
export interface OverlayDistributionMap {
	[key: string]: { [key: string]: OverlayDistribution }
}
//export interface OverlayDistributionMap {
//	[key: string]: OverlayDistribution
//}

export class ScalarFieldManager extends WorkerManager {

	private isolineCache: IsolineCache;
	private updateWindFieldProfile: (profile: WindProfileInterval[]) => void;
	private updateWindField: (time: number | undefined) => void;
	private svgConsumer: (id: string, time: number, svg: MapOverlaySVGResult, fresh: boolean) => void;
	private impactConsumer: (cancheEntry: CacheEntry) => void;
	private statusIndicator: ScalarFieldManagerStatusIndicator;
	private loadingIndicator: LoadingStatusIndicator;
	private scriptIntervalMSWidth: number;

	private mapLayout: CombineMode;
	private svgRenderColor: ColorMode;
	private svgRenderMode: RenderMode;
	private projectsToRender: string[];
	private smoothen: boolean;
	private flatness: number


	private cacheProcessor: (cacheEntry: CacheEntry, grid: ModellingGrid) => void = undefined;

	private scalarsObservable: Observable<ScalarMap>;
	private cacheObservable: Observable<CacheKey[]>;
	
	private authenticated:boolean;

	/**
	 * When this method is defined you can call it to render the scalar fields on the map. Internally 
	 * this method is called when the timeline shifts and new data might be present. This method is
	 * also called when the user changes the mapLayout with the force parameter set to true.
	 * This method is the bound version of <code>ScalarFieldManager.renderModelProjects</code> .
	 * @param force when set to true, all isolines will be recalculated. Defaults to false
	 */
	private renderMap: (force?: boolean) => void;//Promise<CacheKey[]>;
	private mapSVGCacheEntry: (isolineCacheEntry: CacheEntry) => { timeKey: number, overlayElemIds: string[], svg: MapOverlaySVGResult };


	constructor(private application: Application, private grid: ModellingGrid,  private onAuthentication: ( authenticated:boolean) => void ) {
		super(new ScalarFieldWorker());

		this.mapLayout = CombineMode.all;
		this.svgRenderColor = ColorMode.palette;
		this.svgRenderMode = RenderMode.line_band;
		this.projectsToRender = undefined;
		this.smoothen = true;
		this.flatness = 5.0;//in metres

		this.renderMap = undefined;
		this.mapSVGCacheEntry = undefined;

       this.authenticated = false;
	}
	
	public getGrid():ModellingGrid{
		return this.grid;
	}

	protected beforeListeners(): void {
		this.loadingIndicator = new LoadingStatusIndicator();
		this.isolineCache = new IsolineCache();
		this.statusIndicator = undefined;
		this.svgConsumer = undefined;
	    this.updateWindFieldProfile = undefined;
	    this.updateWindField = undefined;

		this.impactConsumer = undefined;
	}


	protected addListeners() {

		this.addListener('authorized', () => {
			console.info('authorized');
			this.authenticated = true;
			if(this.onAuthentication){
				this.onAuthentication(this.authenticated);
			}
		});
		this.addListener('unauthorized', () => {
			console.info('unauthorized');
			this.authenticated = false;
			if(this.onAuthentication){
				this.onAuthentication(this.authenticated);
			}
		});
		this.addListener('itemLoaded', (modelRunKey: string) => {
			this.loadingIndicator.setLoaded(modelRunKey);
			if (this.getStatusIndicator()) {
				this.getStatusIndicator().updateLoadingStatus(this.loadingIndicator.getLoadingStatusProfile());
			}
		});

		this.addListener('representationCreationResponse', (isolineResult: CacheEntry) => {
			if (isolineResult) {
				if (this.getCacheProcessor()) {
					this.getCacheProcessor()(isolineResult, this.grid);
				}
				this.isolineCache.update(isolineResult);
				this.renderTimeKey(isolineResult.timeKey);
			}
		});

		this.addListener('setImpactMonitorsResponse', (impactMonitors: ({
			id: string;
			definition: Polygon;
		} | {
			id: string;
			definition: Point2D;
		})[]
			//, updatedCache: Map<number, CacheEntry>
		) => {
			//console.log('setImpactMonitorsResponse %o\nupdatedCache:  %o', impactMonitors, updatedCache);
			//console.log('setImpactMonitorsResponse %o\n', impactMonitors);
		});

		this.scalarsObservable = new Observable<ScalarMap>(subscriber => {
			this.addListener('scalarsResponse', (scalarMap: ScalarMap) => {
				try {
					subscriber.next(scalarMap);
				} catch (err) {
					subscriber.error(err); // delivers an error if it caught one
				}
			});
		});

		this.cacheObservable = new Observable<CacheKey[]>(subscriber => {
			this.addListener('representationCreated', (isolineResult: CacheEntry) => {
				if (this.getCacheProcessor()) {
					this.getCacheProcessor()(isolineResult, this.grid);
				}
				this.isolineCache.update(isolineResult);
				//				console.log('representationCreated %o', isolineResult);
				if (this.getStatusIndicator()) {
					this.getStatusIndicator().updateCreationStatus(this.isolineCache.getCreationStatusProfile(this.scriptIntervalMSWidth));
				}
				subscriber.next([]);
			});
		});

	}

	private wrapInSVG = (mapOverlaySVGResult: SVGRoot) => wrapInSVG(mapOverlaySVGResult);

	/**
	 * This method converts the isoline representations in SVG to be rendered on the map. This (currently) runs on the fly, but we should consider
	 * also so cachinng the SVG strings ( or even svg doms) 
	 */
	private createSVG = (modelProjects: Map<string, any>, isolineCacheEntry: CacheEntry): { timeKey: number, overlayElemIds: string[], svg: MapOverlaySVGResult } => {
		let combinedSVG = undefined;
		//		if (isolineCacheEntry.representations[this.mapLayout]) {
		if (isolineCacheEntry.representations) {
			combinedSVG = Object.entries(isolineCacheEntry.representations)
				//let combinedSVG = Array.from(isolineCacheEntry.representationMap.entries())
				.filter(isolineMapEntry => isolineMapEntry[1].representation !== undefined)
				.map(isolineMapEntry => {
					
					const firstModelRunKey = <string>isolineMapEntry[1].sources.values().next().value;
					const firstModelProjectId = firstModelRunKey.substring(0, firstModelRunKey.indexOf('/'));

					const modelProject = modelProjects.get(firstModelProjectId);

					let singleHuePalette = getModelingMonoHueInterpolator(modelProject.parameters.render.palette, modelProject.parameters.render.hue);

					let svgOptions = Object.assign({}, modelProject.parameters.render.options, { band: {}, line: {} });
					svgOptions.band = Object.assign({}, modelProject.parameters.render.options.band);
					svgOptions.line = Object.assign({}, modelProject.parameters.render.options.line);


					if (this.svgRenderColor === ColorMode.palette) {
						if (this.svgRenderMode === RenderMode.line) {
							svgOptions.band = undefined
							svgOptions.line.color = svgOptions.line.color && svgOptions.line.color.hue ? svgOptions.line.color : constraintColorInterpolator(getCustomColorInterpolator(modelProject.parameters.render.palette), true);
							svgOptions.line.width = 5;
						} else if (this.svgRenderMode === RenderMode.band) {
							svgOptions.band.color = constraintColorInterpolator( getCustomColorInterpolator(modelProject.parameters.render.palette));
							svgOptions.line.color = constraintColorInterpolator(getCustomColorInterpolator(modelProject.parameters.render.palette));
							svgOptions.line.width = 1;
						} else if (this.svgRenderMode === RenderMode.line_band) {
							svgOptions.band.color = constraintColorInterpolator( getCustomColorInterpolator(modelProject.parameters.render.palette));
							svgOptions.line.color = { hue: 0, saturation: 0, lightness: 80, alpha: 1.0 };
							svgOptions.line.width = 3;
						}
					} else if (this.svgRenderColor === ColorMode.category) {
						if (this.svgRenderMode === RenderMode.line) {
							svgOptions.band = undefined;
							svgOptions.line.color = svgOptions.line.color && svgOptions.line.color.hue ? svgOptions.line.color : constraintColorInterpolator(singleHuePalette, true);
							svgOptions.line.width = 5;
						} else if (this.svgRenderMode === RenderMode.band) {
							svgOptions.band.color = constraintColorInterpolator(singleHuePalette);
							svgOptions.line.color = constraintColorInterpolator(singleHuePalette);
							svgOptions.line.width = 1;
						} else if (this.svgRenderMode === RenderMode.line_band) {
							svgOptions.band.color = constraintColorInterpolator(singleHuePalette);
							svgOptions.line.color = { hue: 0, saturation: 0, lightness: 20, alpha: 1.0 };
							svgOptions.line.width = 3;
						}
					}

					return {
						overlayElemId: isolineMapEntry[0],
						svg: Isoline.toSVG(isolineMapEntry[1].representation, svgOptions, isolineMapEntry[0])
					};
				})
				.sort( (overlayElemSVG0,overlayElemSVG1): number => 
					overlayElemSVG0.svg.range.max==undefined ? -1 : overlayElemSVG1.svg.range.max==undefined ? 1 :
					overlayElemSVG1.svg.range.max - overlayElemSVG0.svg.range.max
				 )
				.reduce((combinedSVG, overlayElemSVG) => {

					if (combinedSVG !== undefined) {
						combinedSVG.overlayElemIds.push(overlayElemSVG.overlayElemId);
						combinedSVG.svg.range.min = combinedSVG.svg.range.min == undefined ? overlayElemSVG.svg.range.min : overlayElemSVG.svg.range.min == undefined ? combinedSVG.svg.range.min : Math.min(combinedSVG.svg.range.min,overlayElemSVG.svg.range.min);
						combinedSVG.svg.range.max = combinedSVG.svg.range.max == undefined ? overlayElemSVG.svg.range.max : overlayElemSVG.svg.range.max == undefined ? combinedSVG.svg.range.max : Math.min(combinedSVG.svg.range.max,overlayElemSVG.svg.range.max);
						combinedSVG.svg.northEast.lat = Math.max(combinedSVG.svg.northEast.lat, overlayElemSVG.svg.northEast.lat);
						combinedSVG.svg.northEast.lng = Math.max(combinedSVG.svg.northEast.lng, overlayElemSVG.svg.northEast.lng);
						combinedSVG.svg.southWest.lat = Math.min(combinedSVG.svg.southWest.lat, overlayElemSVG.svg.southWest.lat);
						combinedSVG.svg.southWest.lng = Math.min(combinedSVG.svg.southWest.lng, overlayElemSVG.svg.southWest.lng);
						let maxX = Math.max(combinedSVG.svg.viewBox.minX + combinedSVG.svg.viewBox.width, overlayElemSVG.svg.viewBox.minX + overlayElemSVG.svg.viewBox.width);
						let maxY = Math.max(combinedSVG.svg.viewBox.minY + combinedSVG.svg.viewBox.height, overlayElemSVG.svg.viewBox.minY + overlayElemSVG.svg.viewBox.height);
						combinedSVG.svg.viewBox.minX = Math.min(combinedSVG.svg.viewBox.minX, overlayElemSVG.svg.viewBox.minX);
						combinedSVG.svg.viewBox.minY = Math.min(combinedSVG.svg.viewBox.minY, overlayElemSVG.svg.viewBox.minY);
						combinedSVG.svg.viewBox.width = maxX - combinedSVG.svg.viewBox.minX;
						combinedSVG.svg.viewBox.height = maxY - combinedSVG.svg.viewBox.minY;
						combinedSVG.svg.content += overlayElemSVG.svg.content;
					} else {
						combinedSVG = {
							timeKey: isolineCacheEntry.timeKey,
							overlayElemIds: [overlayElemSVG.overlayElemId],
							svg: {
								content: overlayElemSVG.svg.content,
								viewBox: {
									minX: overlayElemSVG.svg.viewBox.minX,
									minY: overlayElemSVG.svg.viewBox.minY,
									width: overlayElemSVG.svg.viewBox.width,
									height: overlayElemSVG.svg.viewBox.height
								},
								southWest: {
									lat: overlayElemSVG.svg.southWest.lat,
									lng: overlayElemSVG.svg.southWest.lng
								},
								northEast: {
									lat: overlayElemSVG.svg.northEast.lat,
									lng: overlayElemSVG.svg.northEast.lng
								},
								range:  {
									min: overlayElemSVG.svg.range!=undefined ?  overlayElemSVG.svg.range.min : undefined,
									max: overlayElemSVG.svg.range!=undefined ?  overlayElemSVG.svg.range.max : undefined,
								} ,
								build: {
									start: overlayElemSVG.svg.build.start,
									end: overlayElemSVG.svg.build.end,
									time: overlayElemSVG.svg.build.time
								}
							}
						};
					}

					return combinedSVG;
				}, <{ timeKey: number, overlayElemIds: string[], svg: ScalarFieldSVGResult }>undefined);
		}
		if (combinedSVG) {
			combinedSVG.svg.content = this.wrapInSVG(combinedSVG.svg);
			return { timeKey: combinedSVG.timeKey, overlayElemIds: combinedSVG.overlayElemIds, svg: markupToSVGElemResult(combinedSVG.svg) };
		} else {
			return undefined;
		}

	}

	public authenticate =  (signInUrl: string, credentials: string ) => {
		this.postMessage('authenticate', signInUrl, credentials);
	}

	public setModes(layout: CombineMode, renderMode: RenderMode, colorSchema: ColorMode, projectsToRender: string[]): ScalarFieldManager {
		let sameProjectsToRender = this.projectsToRender == undefined && projectsToRender == undefined;
		if (!sameProjectsToRender && projectsToRender && this.projectsToRender && projectsToRender.length === this.projectsToRender.length) {
			sameProjectsToRender = this.projectsToRender.reduce((same: boolean, projectId: string) => {
				same = same && projectsToRender.includes(projectId)
				return same;
			}, true);
		}

		if (this.mapLayout != layout ||
			this.svgRenderMode != renderMode ? renderMode : RenderMode.band ||
			this.svgRenderColor != colorSchema || !sameProjectsToRender) {

			this.svgRenderMode = renderMode ? renderMode : RenderMode.band;
			this.svgRenderColor = colorSchema;
			this.projectsToRender = projectsToRender;

			if (this.mapLayout != layout || !sameProjectsToRender) {
				this.mapLayout = layout;
				if (this.renderMap) {
					this.renderMap(true);
				}
			}
		}
		this.updateVisualisation();
		return this;
	}

	public getModes(): { layout: CombineMode, renderMode: RenderMode, colorSchema: ColorMode, projectsToRender: string[] } {
		return { layout: this.mapLayout, renderMode: this.svgRenderMode, colorSchema: this.svgRenderColor, projectsToRender: this.projectsToRender };
	}

	private updateVisualisation() {
		const timeRange = this.application.getMainTimeline().getTimeRange(true);
		const time = timeRange.cursor ? timeRange.cursor.center : undefined;
		if (!this.renderTimeKey(time)) {
			this.postMessage('representationCreationRequest', time);
		}
	}
	private renderTimeKey(timeKey: number): boolean {
		if (this.svgConsumer || this.impactConsumer || this.updateWindField) {
			if (timeKey != undefined) {
				let isolineCacheEntry = this.isolineCache.get(timeKey);
				if (isolineCacheEntry) {
					if (this.svgConsumer && this.mapSVGCacheEntry) {
						let mappedCacheEntry = this.mapSVGCacheEntry(isolineCacheEntry);
						this.svgConsumer('id', timeKey, mappedCacheEntry ? mappedCacheEntry.svg : undefined, true);
					}
					if (this.impactConsumer) {
						this.impactConsumer(isolineCacheEntry);
					}
					if (this.updateWindField) {
						this.updateWindField(timeKey);
					}
					return true;
				} else {
					return false;
				}
			} else {
				if(this.svgConsumer) this.svgConsumer('id', timeKey, undefined, true);
				if(this.impactConsumer) this.impactConsumer(undefined);
				if(this.updateWindField) this.updateWindField(undefined);
				return true
			}
		} else {
			return timeKey != undefined;
		}
	}

	public getImpacts(time: number, id: string): ImpactMap {
		if (this.isolineCache) {
			let cacheEntry = this.isolineCache.get(time);
			if (cacheEntry && cacheEntry.representations && cacheEntry.representations[id]) {
				return cacheEntry.representations[id].impacts;
			}
		}
		return undefined
	}
	public setWindFieldConsumer( 
//		setWindField:  (hpc: string, model: string) => void,
		 updateWindFieldProfile: (profile: WindProfileInterval[]) => void,
		  updateWindField: (time: number | undefined) => void
		 ){
//		this.setWindField = setWindField;
		this.updateWindFieldProfile = updateWindFieldProfile;
		this.updateWindField = updateWindField;
	}

	public setSVGConsumer(svgConsumer: (id: string, time: number, svg: MapOverlaySVGResult, fresh: boolean) => void): void {
		this.svgConsumer = svgConsumer;
	}
	public setImpactConsumer(impactConsumer: (cacheEntry: CacheEntry) => void): void {
		this.impactConsumer = impactConsumer;
	}
	public setStatusIndicator(statusIndicator: ScalarFieldManagerStatusIndicator) {
		this.statusIndicator = statusIndicator;
	}
	private getStatusIndicator(): ScalarFieldManagerStatusIndicator {
		return this.statusIndicator;
	}
	public setCachProcessor(cacheProcessor: (cacheEntry: CacheEntry, grid: ModellingGrid) => void) {
		this.cacheProcessor = cacheProcessor;
	}
	private getCacheProcessor(): (cacheEntry: CacheEntry, grid: ModellingGrid) => void {
		return this.cacheProcessor;
	}
	public requestScalars(locationTime: LocationTime): void {
		this.postMessage('scalarsRequest', locationTime);
	}

	public getScalarsObservable(): Observable<ScalarMap> {
		return this.scalarsObservable;
	}

	public getCacheObservable(): Observable<CacheKey[]> {
		return this.cacheObservable;
	}

	public setImpactMonitors(impactMonitors: ImpactMonitorDatum[]) {
		//		this.postMessage('setImpactMonitors', impactMonitors, this.isolineCache.getCache());
		
		this.postMessage('setImpactMonitors', processImpactMonitors(impactMonitors ,this.grid));
	}

	/**
	 * Gets called by the data channel when timerange has changed
	 * @param modelProjects the model projects that make up the profile
	 * @param profile the modeling profile (which project/run ran at which timestamp)
	 * @param animationKeys the series of cursor positions(millis) that will be used during animation
	 * @param animationInterval the animation interval in millis
	 */
	public handleData(projectType:string, modelProjects: ModelProject[], profile: ModelRunProfile[], categorizedModelProjects: Map<string, ModelProject[]>, animationKeys: number[], animationInterval: number): void {
		//console.log(`ScalarFieldManager handle data for type ${projectType}`)
		
		if(modelProjects && modelProjects.length > 0 ){
//			const hpc = modelProjects[0].parameters.hpc;
//			const model = modelProjects[0].parameters.model;
			const windProfile = profile.map( profileEntry => {
				return {
					start: profileEntry.start,
					end: profileEntry.end,
					version: profileEntry.properties.runs ? Object.values( profileEntry.properties.runs).reduce( (version,run)=>{
						return Math.max(version,run.version);
					},0) : undefined
				}
			});
//			console.log('windProfile',windProfile);
			if(this.updateWindField){
				this.updateWindFieldProfile(windProfile);
			}
		}
		
		this.interruptWorker()
			.then(() => {
				let projectMap = modelProjects.reduce((map, project) => { map.set(project.id, project); return map; }, new Map<string, any>());
				this.mapSVGCacheEntry = this.createSVG.bind(this, projectMap);
				this.renderMap = this.renderModelProjects.bind(this, profile, categorizedModelProjects, animationKeys, animationInterval, this.smoothen, this.flatness, projectMap);
				this.renderMap();

			})
			.catch((error: any) => {
				console.error('Error while handling new modeling profile: %o', error);
				this.renderMap = undefined;
				this.mapSVGCacheEntry = undefined;
			});
	}

	private renderModelProjects(profile: ModelRunProfile[], categorizedModelProjects: Map<string, ModelProject[]>, animationTimeKeys: number[], animationInterval: number, smoothen: boolean, flatness: number, projectMap: Map<string, any>, force = false): void {

		let modelProjectIds = this.projectsToRender ? Array.from(projectMap.keys()).filter(projectId => this.projectsToRender.includes(projectId)) : Array.from(projectMap.keys());
		let modelProjects = modelProjectIds.map(projectId => projectMap.get(projectId));
		this.interruptWorker()
			.then(() => {

				//determine how to combine the projects (called the distribution)
				let overlayDistributionProfileMap = this.getMapOverlayDistributionAndProfiles(modelProjectIds, profile, categorizedModelProjects);

				let visibleProfile = this.extractLocalProfile(profile, modelProjectIds);
				let cacheKeyMap: Map<string, LoadIndicatedCacheKey> = this.loadingIndicator.setProfile(visibleProfile);
				let isolineCacheMap = this.isolineCache.invalidate(animationTimeKeys, overlayDistributionProfileMap.distribution[this.mapLayout], cacheKeyMap, modelProjects, force);

				this.scriptIntervalMSWidth = animationInterval;

				if (this.getStatusIndicator()) {
					this.getStatusIndicator().updateLoadingStatus(this.loadingIndicator.getLoadingStatusProfile());
					this.getStatusIndicator().updateCreationStatus(this.isolineCache.getCreationStatusProfile(this.scriptIntervalMSWidth));
				}

				let stationairyTimeCursorPosition = undefined;
				if (!this.application.getMainTimeline().isAnimating()) {
					const timeRange = this.application.getMainTimeline().getTimeRange(true);
					if (timeRange.cursor) {
						stationairyTimeCursorPosition = timeRange.cursor.center;
					}
				}

				this.postMessage('handleData', modelProjects, overlayDistributionProfileMap.profile, smoothen, flatness, cacheKeyMap, isolineCacheMap, animationTimeKeys, stationairyTimeCursorPosition);

				this.updateVisualisation();

			})
			.catch((error: any) => {
				console.error('Error while rendering modeling profile: %o', error);
			});
	}

	private getMapOverlayDistributionAndProfiles(modelProjectIds: string[], profile: ModelRunProfile[], categorizedModelProjects: Map<string, ModelProject[]>): { distribution: OverlayDistributionMap, profile: ProfileMap } {
		let overlayDistributionMap = this.getOverlayDistributions(modelProjectIds, categorizedModelProjects);
		let profilesMap = this.geOverlaytProfiles(overlayDistributionMap, profile);

		return { distribution: overlayDistributionMap, profile: profilesMap };
	}
	public getOverlayDistributions(modelProjects: string[] | ModelProject[], categorizedModelProjects: Map<string, ModelProject[]>, combineMode?: CombineMode): OverlayDistributionMap {
		let modelProjectIds: string[] = modelProjects.length > 0 ? (<string[]>modelProjects)[0].toLocaleLowerCase ? <string[]>modelProjects : (<ModelProject[]>modelProjects).map(mp => mp.id) : [];

		let overlayDistributions: OverlayDistributionMap = {};

		overlayDistributions[CombineMode.all] = (combineMode === undefined || combineMode === CombineMode.all) ? {
			all: {
				elemId: 'all',
				//dataIds: modelProjects.map(mp => <string>mp.id)
				dataIds: modelProjectIds
			}
		} : undefined;

		overlayDistributions[CombineMode.category] = (combineMode === undefined || combineMode === CombineMode.category) ? Array.from(categorizedModelProjects.entries())
			.reduce((categoryOverlayIdMap, categoryEntry) => {
				categoryOverlayIdMap[categoryEntry[0]] = {
					elemId: categoryEntry[0],
					//elemId: 'cat_' + categoryEntry[0],
					dataIds: categoryEntry[1].filter((mp) => modelProjectIds.includes(mp.id)).map((mp) => <string>mp.id)
				};
				return categoryOverlayIdMap;
			}, <{ [key: string]: OverlayDistribution }>{}) : undefined;

		overlayDistributions[CombineMode.none] = (combineMode === undefined || combineMode === CombineMode.none) ? modelProjectIds.reduce((noneOverlayIdMap, modelProjectId) => {
			noneOverlayIdMap[modelProjectId] = {
				elemId: modelProjectId,
				//elemId: 'prj_' + modelProject.id,
				dataIds: [modelProjectId]
			};
			return noneOverlayIdMap;
		}, <{ [key: string]: OverlayDistribution }>{}) : undefined;

		return overlayDistributions;
	}

	private geOverlaytProfiles(overlayDistributionMap: OverlayDistributionMap, profile: ModelRunProfile[]): ProfileMap {
		return this.getProfileMap(overlayDistributionMap[this.mapLayout], profile);
	}

	private getProfileMap(overlayDistributionMap: { [key: string]: OverlayDistribution }, profile: ModelRunProfile[]): Map<string, ModelRunProfile[]> {
		return Object.values(overlayDistributionMap).reduce((map, overlayDistribution) => {
			map.set(overlayDistribution.elemId, this.extractLocalProfile(profile, overlayDistribution.dataIds))
			return map;
		}, new Map<string, ModelRunProfile[]>())

	}

	/**
	 * Extracts from given profile the profile for given modelProjectIds
	 * @param profile the profile where to extract from
	 * @param modelProjectIds the projectIds where to get the profile for
	 */
	private extractLocalProfile(profile: ModelRunProfile[], modelProjectIds: string[]): ModelRunProfile[] {
		let filterProjectId: (modelProjectId: string) => boolean =
			(modelProjectIds && modelProjectIds.length > 0) ? (modelProjectId: string) => modelProjectIds.indexOf(modelProjectId) != -1
				: (_modelProjectId: string) => true;

		return profile.map(modelRunProfile => {
			return {
				start: modelRunProfile.start,
				end: modelRunProfile.end,
				properties: {
					runs: Object.keys(modelRunProfile.properties.runs)
						.filter(filterProjectId)
						.map(modelProjectId => { return { modelProjectId: modelProjectId, modelRunProfile: modelRunProfile.properties.runs[modelProjectId] }; })
						.reduce((runs, modepProjectRunProfile) => {
							runs[modepProjectRunProfile.modelProjectId] = Object.assign({}, modepProjectRunProfile.modelRunProfile);
							return runs
						}, <{ [key: string]: ModelRun }>{})
				}
			};
		});
	}

	private interruptWorker() {
		return new Promise<void>((resolve, _reject) => {
			this.addListener('interrupted', () => {
				resolve();
			});
			this.postMessage('interrupt');
		});
	}
}

class IsolineCache {
	//maps the center timeCursor millis visited during animation to the Isolines to be displayed at that interval.
	private cache: Map<number, CacheEntry>;

	constructor() {
		this.cache = new Map();
	}

	public getCreationStatusProfile(scriptIntervalMSWidth: number) {
		const halfWidthMS = scriptIntervalMSWidth / 2;
		return Array.from(this.cache.values()).map(cacheEntry => {
			let available = Object.values(cacheEntry.representations).reduce((available, isolineMapEntry) => available && isolineMapEntry.representation !== undefined, true);
			let start = cacheEntry.timeKey - halfWidthMS;
			let end = start + scriptIntervalMSWidth;
			return { start: start, end: end, available: available };
		});
	}

	public get(timeKey: number): CacheEntry {
		return this.cache.get(timeKey);
	}
	public update(isolineResult: CacheEntry): void {
		let cacheEntry = this.cache.get(isolineResult.timeKey);
		if (cacheEntry) {
			//the merge cashed results that were fresh with the new results
			//			Object.keys(cacheEntry.representations).forEach(combineKey => {
			Object.entries(cacheEntry.representations).forEach(entry => {
				if (entry[1].sources && isolineResult.representations && !isolineResult.representations[entry[0]]) {
					isolineResult.representations[entry[0]] = entry[1];
				}
			});
			//			});
		}
		this.cache.set(isolineResult.timeKey, isolineResult);
	}

	public getCache(): Map<number, CacheEntry> {
		return this.cache;
	}


	/**
	 * 
	 * @param animationTimeKeys an array of all the timeCursor center millis that will be visited during animation
	 * @param distribution
	 * @param cacheKeyMap
	 * @param modelProjects
	 * @param force
	 */

	//	public invalidate(animationTimeKeys: number[], distributions: OverlayDistributionMap, cacheKeyMap: Map<string, LoadIndicatedCacheKey>, modelProjects: any[], force = false): Map<number, CacheEntry> {
	public invalidate(animationTimeKeys: number[], distributions: { [key: string]: OverlayDistribution }, cacheKeyMap: Map<string, LoadIndicatedCacheKey>, modelProjects: any[], force = false): Map<number, CacheEntry> {
		//a complete re-render is requested
		if (force) {
			this.cache.clear();
		}

		//remove all entries that do not exist in the list of animationTimeKeys
		this.cache.forEach((value, key, map) => { if (animationTimeKeys.indexOf(value.timeKey) === -1) map.delete(key) });

		//remove all isolineMap entries that do not occur in the distribution
		Array.from(this.cache.values())
			.forEach(cacheEntry => {
				//				Object.keys(cacheEntry.representations).forEach(combineKey => {
				let representationDefinitions = Object.values(distributions);
				Object.keys(cacheEntry.representations).forEach(elemId => {
					if (representationDefinitions.findIndex(tlOverlayElem => tlOverlayElem.elemId === elemId) === -1) {
						delete cacheEntry.representations[elemId];
					}
				});
				//				})
			})

		//		//flag unavailable when a cache key is unavailable or needs to be loaded
		//		Array.from(cacheKeyMap.values())
		//			.filter(cacheKey => !cacheKey.available || cacheKey.load)
		//			.forEach(cacheKey => {
		//				Array.from(this.cache.values())
		//					.forEach(cacheEntry => {
		//						Object.keys(cacheEntry.representations).forEach(combineKey => {
		//
		//							Object.values(cacheEntry.representations[combineKey]).forEach(value => {
		//								if (value.sources && value.sources.has(cacheKey.key)) {
		//									value.sources = undefined;//no sources/ no isoline signals refresh for the isoline factory
		//									value.representation = undefined;
		//								}
		//							});
		//						})
		//					})
		//			});



		//The code below does take fade in/out effects into account!! (the commented section above does.)  	
		//maybe the code below holds the answer for this problem. The code above does not work for new projects
		//while animation is already running. 

		//remove all entries in the same time range of cache keys that are unavailable or need to be loaded
		Array.from(cacheKeyMap.values())
			.filter(cacheKey => !cacheKey.available || cacheKey.load)
			.forEach(cacheKey => {
				Array.from(this.cache.keys())
					.filter(timeKey => timeKey >= cacheKey.start && timeKey < cacheKey.end)
					.forEach(timeKey => this.cache.delete(timeKey));
			});


		//add all missing entries
		animationTimeKeys
			.forEach(timeKey => {
				if (!this.cache.get(timeKey)) {
					//fill isolineMap
					const activeOverlays = this.findActiveOverlays(timeKey, distributions, modelProjects);
					//const activeOverlays = this.findActiveOverlays(timeKey, distributions, modelProjects);
					let isolineMap: { [key: string]: RepresentationDefinition } = {};


					//					Object.keys(activeOverlayMap).forEach(combineKey => {
					activeOverlays.forEach(overlayElemId => isolineMap[overlayElemId] = { sources: undefined, representation: undefined, impacts: undefined });
					this.cache.set(timeKey, { timeKey: timeKey, representations: isolineMap });
					//					});

				}
			});

		//now return a copy of the cash without the isolines and return
		let cacheClone = new Map<number, CacheEntry>();

		this.cache.forEach((cacheEntry, timestamp) => {

			let cacheEntryClone: CacheEntry = { timeKey: timestamp, representations: {} };

			//		Object.keys(cacheEntry.representations).forEach(combineKey => {


			let isolineSourceMap = Object.entries(cacheEntry.representations)
				.map(isolineEntry => { return { key: isolineEntry[0], value: { sources: isolineEntry[1].sources, representation: undefined, impacts: undefined } }; })
				.reduce((representationMap, entry) => {
					representationMap[entry.key] = entry.value;
					return representationMap;
				}, <{ [key: string]: RepresentationDefinition }>{});
			cacheEntryClone.representations = isolineSourceMap;
			//			});

			cacheClone.set(timestamp, cacheEntryClone);
		});

		return cacheClone;
	}


	//	private findActiveOverlays(timeKey: number, distributions: OverlayDistributionMap, modelProjects: any[]): { [key: string]: string[] } {
	//		return Object.entries(distributions).reduce((activeOverlayMap, distrubutionEntry) => {
	//			let combineKey = distrubutionEntry[0];
	//			let distribution = distrubutionEntry[1];
	//			activeOverlayMap[combineKey] = Object.values(distribution).filter(overlay =>
	//				overlay.dataIds.reduce((active, modelProjectId) => {
	//					const projectIsActive = modelProjects.find(modelProject => modelProject.id === modelProjectId && modelProject.start <= timeKey && modelProject.end > timeKey) !== -1
	//					return active || projectIsActive;
	//				}, false)
	//			)
	//				.map(overlay => overlay.elemId)
	//			return activeOverlayMap;
	//		}, <{ [key: string]: string[] }>{});
	//
	//	}

	//	private findActiveOverlays(timeKey: number, distribution: OverlayDistributionMap, modelProjects: any[]): string[]  {
	private findActiveOverlays(timeKey: number, distribution: { [key: string]: OverlayDistribution }, modelProjects: any[]): string[] {
		return Object.values(distribution).filter(overlay =>
			overlay.dataIds.reduce((active, modelProjectId) => {
				const projectIsActive = modelProjects.find(modelProject => modelProject.id === modelProjectId && modelProject.start <= timeKey && modelProject.end > timeKey) !== -1
				return active || projectIsActive;
			}, false)
		)
			.map(overlay => overlay.elemId)
	}
}

interface ModelRunLoadingProfile extends ModelRunProfile {
	properties: {
		runs: { [key: string]: ModelRun & { available: boolean } }
	}
}

class LoadingStatusIndicator {
	private profile: ModelRunLoadingProfile[];

	constructor() {
		this.profile = [];
	}

	public setLoaded(modelRunKey: string) {
		this.profile.forEach(modelRunProfile => {
			return Object.entries(modelRunProfile.properties.runs)
				.forEach(entry => {
					let key = `${entry[0]}/${entry[1].runId}`; //modelProjectId/modelRunId
					if (key === modelRunKey) {
						modelRunProfile.properties.runs[entry[0]].available = true;
					}
				})
		});
	}

	public getLoadingStatusProfile() {
		return this.profile.map(modelRunProfile => {
			let available = Object.values(modelRunProfile.properties.runs).reduce((available, run) => available && run.available, true);
			return { start: modelRunProfile.start, end: modelRunProfile.end, available: available };
		});
	}

	public setProfile(profile: ModelRunProfile[]): Map<string, LoadIndicatedCacheKey> {
		//extract all modelRun keys (in the form of CacheKey s) from the current profile and put them in a map.
		let oldModelRunKeys = this.extractModelRunKeys(this.profile)
			.reduce((map, modelRunProfile) => { map.set(modelRunProfile.key, modelRunProfile); return map; }, new Map<string, LoadIndicatedCacheKey>());

		//helper function to determine if a modelRun should be (re-)loaded.
		const load = (ck0: CacheKey, ck1: LoadIndicatedCacheKey) => {

			return ck0.available && (
				ck1 === undefined ||
				ck1.load ||
				!(ck0.start === ck1.start && ck0.end === ck1.end && ck0.version === ck1.version && ck0.available === ck1.available)
			);
		}

		//extract all modelRun keys (in the form of CacheKey s) from the (new) visible profile and put them in a map.
		//these keys also have a flag that indicates that the modelrun needs to be (re-)loaded
		let newModelRunKeys = this.extractModelRunKeys(<ModelRunLoadingProfile[]>profile).map(modelRunKey => <LoadIndicatedCacheKey>Object.assign({}, modelRunKey, { load: load(modelRunKey, oldModelRunKeys.get(modelRunKey.key)) }))
			.reduce((map, modelRunProfile) => { map.set(modelRunProfile.key, modelRunProfile); return map; }, new Map<string, LoadIndicatedCacheKey>());

		//construct the new loading status profile. This profile is a clone of the new visible profile, injected with an
		//'available' flag for each model run entry. 
		//N.B. The flag 'available' is set to false when the new scalar field for the model run has not been loaded yet. When
		//the 'status' field' does not equal 'ready' then available is set to true but a scalar field will not be loaded / present
		//either.
		let newProfile = profile.map(modelRunProfile => {
			return Object.entries(modelRunProfile.properties.runs)
				.map(entry => {
					let modelRunKey = `${entry[0]}/${entry[1].runId}`; //modelProjectId/modelRunId
					return { key: entry[0], value: Object.assign({ available: !newModelRunKeys.get(modelRunKey).load }, entry[1]) };
				})
				.reduce((mrlp, runEntry) => {
					mrlp.properties.runs[runEntry.key] = runEntry.value;
					return mrlp;
				}, <ModelRunLoadingProfile>Object.assign({}, modelRunProfile, { properties: { runs: {} } }))
		});

		this.profile = newProfile;

		return newModelRunKeys;
	}

	private extractModelRunKeys(profile: ModelRunLoadingProfile[]): LoadIndicatedCacheKey[] {
		let modelRunKeys: LoadIndicatedCacheKey[] = [];

		new Set(
			profile.flatMap(modelRunProfile =>
				Object.entries(modelRunProfile.properties.runs)
					.map(entry => {
						return {
							key: `${entry[0]}/${entry[1].runId}`, //modelProjectId/modelRunId
							projectId: entry[0],
							runId: entry[1].runId,
							start: modelRunProfile.start,
							end: modelRunProfile.end,
							range:  entry[1].range,
							version: entry[1].version,
							available: entry[1].status === 'ready' || entry[1].status === 'pending-refresh',
							load: entry[1].available !== true && (entry[1].status === 'ready' || entry[1].status === 'pending-refresh')
						};
					})
			)
		).forEach(modelRunKey => modelRunKeys.push(modelRunKey));

		return modelRunKeys;
	}

}

export interface ScalarFieldManagerStatusIndicator {
	updateCreationStatus(creationProfile: { start: number, end: number, available: boolean }[]): void,
	updateLoadingStatus(loadingProfile: { start: number, end: number, available: boolean }[]): void
}
